import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

import { fetchAuth } from "./api";
import { useSeason } from "./SeasonContext";

const GlobalStatsContext = createContext(null);
const EMPTY_STATE = {
  overviewBySeason: {},
  syncReportBySeason: {},
  errorBySeason: {},
  loading: false,
};

async function readApiError(response, fallbackMessage) {
  try {
    const payload = await response.json();
    return payload?.error || payload?.message || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
}

export function GlobalStatsProvider({
  children,
  token,
  refreshIntervalMs = 12 * 60 * 60 * 1000,
}) {
  const { season } = useSeason();
  const [state, setState] = useState(EMPTY_STATE);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const refetchRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let intervalId;

    const seasonKey = season || "__all__";

    async function fetchOverview() {
      if (!token) {
        if (!cancelled) {
          setState(EMPTY_STATE);
        }
        return;
      }

      if (!cancelled) {
        setState((prev) => ({
          ...prev,
          loading: !prev.overviewBySeason[seasonKey],
          errorBySeason: {
            ...prev.errorBySeason,
            [seasonKey]: null,
          },
        }));
      }

      try {
        const response = await fetchAuth(`/players/global-stats-overview-flashscore?season=${encodeURIComponent(season || "")}&sync_missing=false`);

        if (!response.ok) {
          throw new Error(
            await readApiError(response, "Unable to load global stats overview."),
          );
        }

        const payload = await response.json();

        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            loading: false,
            overviewBySeason: {
              ...prev.overviewBySeason,
              [seasonKey]: payload,
            },
            syncReportBySeason: {
              ...prev.syncReportBySeason,
              [seasonKey]: payload?.sync_report || null,
            },
            errorBySeason: {
              ...prev.errorBySeason,
              [seasonKey]: null,
            },
          }));
        }
      } catch (error) {
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            loading: false,
            errorBySeason: {
              ...prev.errorBySeason,
              [seasonKey]: error,
            },
          }));
        }
      }
    }

    refetchRef.current = fetchOverview;

    if (!token) {
      setState(EMPTY_STATE);
      return () => {};
    }

    fetchOverview();
    intervalId = setInterval(fetchOverview, refreshIntervalMs);

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [refreshIntervalMs, season, token]);

  const seasonKey = season || "__all__";

  const hydrate = async ({
    refresh = false,
    syncMissing = true,
    includeDisabled = false,
    includeDetails = true,
  } = {}) => {
    if (!token) {
      return null;
    }

    setIsSyncing(true);
    setSyncError(null);

    try {
      const response = await fetchAuth("/players/global-stats-overview-flashscore/sync", {
        method: "POST",
        body: JSON.stringify({
          refresh,
          sync_missing: syncMissing,
          include_disabled: includeDisabled,
          include_details: includeDetails,
        }),
      });

      if (!response.ok) {
        throw new Error(
          await readApiError(response, "Unable to synchronize Flashscore global stats."),
        );
      }

      const payload = await response.json();

      setState((prev) => ({
        ...prev,
        syncReportBySeason: {
          ...prev.syncReportBySeason,
          [seasonKey]: payload,
        },
      }));

      if (refetchRef.current) {
        await refetchRef.current();
      }

      return payload;
    } catch (error) {
      setSyncError(error);
      throw error;
    } finally {
      setIsSyncing(false);
    }
  };

  const value = useMemo(
    () => ({
      season,
      overview: state.overviewBySeason[seasonKey] || null,
      syncReport: state.syncReportBySeason[seasonKey] || null,
      loading: state.loading,
      error: state.errorBySeason[seasonKey] || null,
      isSyncing,
      syncError,
      hydrate,
      refetch: async () => {
        if (refetchRef.current) {
          return await refetchRef.current();
        }
      },
    }),
    [hydrate, isSyncing, season, seasonKey, state, syncError],
  );

  return (
    <GlobalStatsContext.Provider value={value}>
      {children}
    </GlobalStatsContext.Provider>
  );
}

export function useGlobalStats() {
  const ctx = useContext(GlobalStatsContext);
  if (!ctx) {
    throw new Error("useGlobalStats must be used inside <GlobalStatsProvider />");
  }
  return ctx;
}
